import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import userService from '../services/userService';
import type { 
  User, 
  UserFilters, 
  UserSubmitRequest,
  UserUpdateRequest,
  GetUsersParams,
  UsersResponse,
  BulkUpdateRequest,
  BulkUpdateResult
} from '../types';

export const useUsers = (params: GetUsersParams = {}) => {
  const [filters, setFilters] = useState<UserFilters>({});
  const [page, setPage] = useState(params.page || 1);
  const [perPage, setPerPage] = useState(params.per_page || 15);

  const queryClient = useQueryClient();

  // Fetch users
  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['users', { ...filters, page, perPage }],
    queryFn: () => userService.getUsers({ ...filters, page, per_page: perPage }),
    placeholderData: (previousData) => previousData
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: UserSubmitRequest) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Toast will be handled by the component that calls this mutation
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tạo người dùng');
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateRequest }) => 
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Cập nhật người dùng thành công!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật người dùng');
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Xóa người dùng thành công!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi xóa người dùng');
    }
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (data: BulkUpdateRequest) => userService.bulkUpdateUsers(data),
    onSuccess: (result: BulkUpdateResult) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (result.success) {
        toast.success(`Cập nhật thành công ${result.updated_count} người dùng!`);
      } else {
        toast.error(`Cập nhật thất bại ${result.failed_count} người dùng`);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật hàng loạt');
    }
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      userService.toggleUserStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Cập nhật trạng thái thành công!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật trạng thái');
    }
  });

  // Helper functions
  const updateFilters = (newFilters: Partial<UserFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page when filters change
  };

  const updatePagination = (newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) {
      setPerPage(newPerPage);
    }
  };

  const resetFilters = () => {
    setFilters({});
    setPage(1);
  };

  const clearFilter = (key: keyof UserFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  return {
    // Data
    users: (usersData as UsersResponse)?.data || [],
    pagination: (usersData as UsersResponse)?.pagination,
    filters,
    page,
    perPage,
    
    // Loading states
    isLoading,
    isError,
    error,
    
    // Filter functions
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,
    
    // Actions
    createUser: createUserMutation.mutate,
    updateUser: updateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
    bulkUpdateUsers: bulkUpdateMutation.mutate,
    toggleUserStatus: toggleStatusMutation.mutate,
    
    // Mutation states
    isCreating: createUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isBulkUpdating: bulkUpdateMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,
    
    // Mutation objects for advanced usage
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,
    bulkUpdateMutation,
    toggleStatusMutation,
    
    // Refetch
    refetch
  };
};

// Key nằm dưới 'users' để các mutation invalidate ['users'] cũng làm mới số liệu.
export const useUserStats = () => {
  return useQuery({
    queryKey: ['users', 'stats'],
    queryFn: () => userService.getStats()
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id),
    enabled: !!id
  });
};

export const useUsersByRole = (role: string, params: GetUsersParams = {}) => {
  return useQuery({
    queryKey: ['users', 'by-role', role, params],
    queryFn: () => userService.getUsersByRole(role, params),
    enabled: !!role
  });
};

export const useUsersBySchool = (schoolId: string, params: GetUsersParams = {}) => {
  return useQuery({
    queryKey: ['users', 'by-school', schoolId, params],
    queryFn: () => userService.getUsersBySchool(schoolId, params),
    enabled: !!schoolId
  });
};

export const useStudents = (params: GetUsersParams = {}) => {
  return useQuery({
    queryKey: ['students', params],
    queryFn: () => userService.getStudents(params)
  });
};

export const useTeachers = (params: GetUsersParams = {}) => {
  return useQuery({
    queryKey: ['teachers', params],
    queryFn: () => userService.getTeachers(params)
  });
};
